import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './Billing.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * Billing - View and manage all billing records and subscriptions
 */
function Billing() {
    const [billings, setBillings] = useState([]);
    const [filteredBillings, setFilteredBillings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, paid, pending, failed
    const [packageFilter, setPackageFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState(''); // today, yesterday, thisMonth, lastMonth, custom
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        totalPages: 1,
        totalRecords: 0,
        limit: 20,
        hasNextPage: false,
        hasPrevPage: false
    });

    useEffect(() => {
        fetchBillingRecords();
    }, [currentPage, statusFilter, packageFilter, dateFilter, searchTerm]);

    const fetchBillingRecords = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');

            const params = new URLSearchParams({
                page: currentPage,
                limit: 20,
                ...(statusFilter !== 'all' && { status: statusFilter }),
                ...(packageFilter !== 'all' && { packageType: packageFilter }),
                ...(dateFilter && { dateFilter }),
                ...(dateFilter === 'custom' && startDate && { startDate }),
                ...(dateFilter === 'custom' && endDate && { endDate }),
                ...(searchTerm && { search: searchTerm })
            });

            const response = await axios.get(`${BASEURL}superadmin/billing/${userId}?${params}`, {
                headers: { token }
            });

            if (response.data.success) {
                setBillings(response.data.data);
                setFilteredBillings(response.data.data);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching billing records:', err);
            setError('Failed to load billing records');
            setLoading(false);
        }
    };

    const handleDateFilterChange = (filter) => {
        setDateFilter(filter);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            paid: styles.statusPaid,
            pending: styles.statusPending,
            failed: styles.statusFailed,
            refunded: styles.statusRefunded
        };

        return (
            <span className={`${styles.statusBadge} ${statusClasses[status] || ''}`}>
                {status || 'unknown'}
            </span>
        );
    };

    const calculateStats = () => {
        const totalRevenue = billings
            .filter(b => b.paymentStatus === 'paid')
            .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

        const pendingAmount = billings
            .filter(b => b.paymentStatus === 'pending')
            .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

        return {
            totalRevenue,
            pendingAmount,
            totalTransactions: billings.length,
            paidTransactions: billings.filter(b => b.paymentStatus === 'paid').length
        };
    };

    if (loading) {
        return (
            <Layout>
                <div className={styles.loading}>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading billing records...
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className={styles.error}>
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                </div>
            </Layout>
        );
    }

    const stats = calculateStats();

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.headerContent}>
                        <h1>Billing Management</h1>
                        <p>View and manage all billing records and subscriptions</p>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className={styles.statsGrid}>
                    <div className={`${styles.statCard} ${styles.revenue}`}>
                        <div className={styles.statIcon}>
                            <i className="fas fa-dollar-sign"></i>
                        </div>
                        <div className={styles.statInfo}>
                            <h3>৳{stats.totalRevenue.toLocaleString()}</h3>
                            <p>Total Revenue</p>
                            <span className={styles.statMeta}>
                                {stats.paidTransactions} paid transactions
                            </span>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.pending}`}>
                        <div className={styles.statIcon}>
                            <i className="fas fa-clock"></i>
                        </div>
                        <div className={styles.statInfo}>
                            <h3>৳{stats.pendingAmount.toLocaleString()}</h3>
                            <p>Pending Amount</p>
                            <span className={styles.statMeta}>
                                Awaiting payment
                            </span>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.transactions}`}>
                        <div className={styles.statIcon}>
                            <i className="fas fa-receipt"></i>
                        </div>
                        <div className={styles.statInfo}>
                            <h3>{stats.totalTransactions}</h3>
                            <p>Total Transactions</p>
                            <span className={styles.statMeta}>
                                All billing records
                            </span>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className={styles.filtersSection}>
                    {/* Date Filter Buttons */}
                    <div className={styles.dateFilters}>
                        <button
                            className={`${styles.dateFilterBtn} ${dateFilter === 'today' ? styles.active : ''}`}
                            onClick={() => handleDateFilterChange('today')}
                        >
                            <i className="fas fa-calendar-day"></i>
                            Today
                        </button>
                        <button
                            className={`${styles.dateFilterBtn} ${dateFilter === 'yesterday' ? styles.active : ''}`}
                            onClick={() => handleDateFilterChange('yesterday')}
                        >
                            <i className="fas fa-calendar-minus"></i>
                            Yesterday
                        </button>
                        <button
                            className={`${styles.dateFilterBtn} ${dateFilter === 'thisMonth' ? styles.active : ''}`}
                            onClick={() => handleDateFilterChange('thisMonth')}
                        >
                            <i className="fas fa-calendar-alt"></i>
                            This Month
                        </button>
                        <button
                            className={`${styles.dateFilterBtn} ${dateFilter === 'lastMonth' ? styles.active : ''}`}
                            onClick={() => handleDateFilterChange('lastMonth')}
                        >
                            <i className="fas fa-calendar"></i>
                            Last Month
                        </button>
                        <button
                            className={`${styles.dateFilterBtn} ${dateFilter === '' ? styles.active : ''}`}
                            onClick={() => handleDateFilterChange('')}
                        >
                            <i className="fas fa-infinity"></i>
                            All Time
                        </button>
                    </div>

                    {/* Custom Date Range */}
                    {dateFilter === 'custom' && (
                        <div className={styles.customDateRange}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className={styles.dateInput}
                            />
                            <span>to</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className={styles.dateInput}
                            />
                        </div>
                    )}

                    <div className={styles.filtersRow}>
                        <div className={styles.searchBox}>
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search by store, user, mobile, or transaction ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className={styles.filterGroup}>
                            <label>Status:</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className={styles.filterSelect}
                            >
                                <option value="all">All Status</option>
                                <option value="paid">Paid</option>
                                <option value="pending">Pending</option>
                                <option value="failed">Failed</option>
                                <option value="refunded">Refunded</option>
                            </select>
                        </div>

                        <div className={styles.filterGroup}>
                            <label>Package:</label>
                            <select
                                value={packageFilter}
                                onChange={(e) => {
                                    setPackageFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className={styles.filterSelect}
                            >
                                <option value="all">All Packages</option>
                                <option value="trial">Trial</option>
                                <option value="smallBusiness">Small Business</option>
                                <option value="business">Business</option>
                                <option value="enterprise">Enterprise</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Billing Table */}
                <div className={styles.tableContainer}>
                    <table className={styles.billingTable}>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Store Name</th>
                                <th>Owner</th>
                                <th>Mobile</th>
                                <th>Package</th>
                                <th>Billing Cycle</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Transaction ID</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBillings.length === 0 ? (
                                <tr>
                                    <td colSpan="10" className={styles.noData}>
                                        <i className="fas fa-inbox"></i>
                                        <div>No billing records found</div>
                                        <small style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px', display: 'block' }}>
                                            Try adjusting your filters or search term
                                        </small>
                                    </td>
                                </tr>
                            ) : (
                                filteredBillings.map((bill) => (
                                    <tr key={bill._id}>
                                        <td>
                                            {new Date(bill.billingDate).toLocaleDateString()}
                                        </td>
                                        <td>
                                            <strong>{bill.storeName || 'N/A'}</strong>
                                        </td>
                                        <td>{bill.userName || 'N/A'}</td>
                                        <td>{bill.mobile || 'N/A'}</td>
                                        <td>
                                            <span className={styles.packageBadge}>
                                                {bill.packageType || 'N/A'}
                                            </span>
                                        </td>
                                        <td className={styles.billingCycle}>
                                            {bill.billingCycle === 'monthly' ? (
                                                <>
                                                    <i className="fas fa-calendar-alt"></i>
                                                    Monthly
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-calendar"></i>
                                                    Yearly
                                                </>
                                            )}
                                        </td>
                                        <td className={styles.amount}>
                                            ৳{bill.amount?.toLocaleString() || 0}
                                        </td>
                                        <td>{getStatusBadge(bill.paymentStatus)}</td>
                                        <td className={styles.transactionId}>
                                            {bill.transactionId || 'N/A'}
                                        </td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button
                                                    className={styles.viewBtn}
                                                    title="View details"
                                                >
                                                    <i className="fas fa-eye"></i>
                                                </button>
                                                <button
                                                    className={styles.downloadBtn}
                                                    title="Download invoice"
                                                >
                                                    <i className="fas fa-download"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className={styles.pagination}>
                        <div className={styles.paginationInfo}>
                            Showing <strong>{((currentPage - 1) * pagination.limit) + 1}</strong> to{' '}
                            <strong>{Math.min(currentPage * pagination.limit, pagination.totalRecords)}</strong> of{' '}
                            <strong>{pagination.totalRecords}</strong> records
                        </div>
                        <div className={styles.paginationControls}>
                            <button
                                className={styles.pageBtn}
                                disabled={!pagination.hasPrevPage}
                                onClick={() => handlePageChange(1)}
                            >
                                <i className="fas fa-angle-double-left"></i>
                            </button>
                            <button
                                className={styles.pageBtn}
                                disabled={!pagination.hasPrevPage}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                <i className="fas fa-angle-left"></i>
                                Prev
                            </button>

                            {/* Page Numbers */}
                            <div className={styles.pageNumbers}>
                                {[...Array(pagination.totalPages)].map((_, i) => {
                                    const pageNum = i + 1;
                                    // Show first page, last page, current page, and 2 pages around current
                                    if (
                                        pageNum === 1 ||
                                        pageNum === pagination.totalPages ||
                                        (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                                    ) {
                                        return (
                                            <button
                                                key={pageNum}
                                                className={`${styles.pageNumber} ${currentPage === pageNum ? styles.active : ''}`}
                                                onClick={() => handlePageChange(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                                        return <span key={pageNum} className={styles.pageDots}>...</span>;
                                    }
                                    return null;
                                })}
                            </div>

                            <button
                                className={styles.pageBtn}
                                disabled={!pagination.hasNextPage}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                Next
                                <i className="fas fa-angle-right"></i>
                            </button>
                            <button
                                className={styles.pageBtn}
                                disabled={!pagination.hasNextPage}
                                onClick={() => handlePageChange(pagination.totalPages)}
                            >
                                <i className="fas fa-angle-double-right"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default Billing;
